import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../../../hooks/useAuth';
import { useLoadingIndicator } from '../../../../hooks/useLoadingIndicator';
import {
	getEligibleCandidatesForNextBatch,
	sendEmailToEligibleCandidatesForNextBatch,
} from '../../../../service/VolunteerApplicationService';
import { useBatchId } from '../../../../hooks/useBatchId';
import { useToastMessage } from '../../../../hooks/useToastMessage';
import { DEFAULT_PAGE_SIZE } from '../../../../shared/Constants';
import {
	ApplicationsFilter,
	BatchFilter,
	GetEligibleCandidatesForNextBatchResponse,
	PagingConfig,
} from '../../../../shared/VolunteerApplicationServiceTypes';
import { useCallApi } from '../../../../hooks/useCallApi';
import { useInfoPopup } from '../../../../hooks/useInfoPopUp';
import ConfirmTick from '../../../../assets/images/confirm-tick.svg';
import { useConfirmPopup } from '../../../../hooks/useConfirmPopup';
import { useNavigate } from 'react-router-dom';
import { getBatches } from '../../../../service/PreWorkService';
import { GetBatchesResponse } from '../../../../shared/PreWorkServiceTypes';
import { EligibleCandidateListState } from './EligibleCandidateList.store';
import { useSnapshot } from 'valtio';
import useUserSession from '../../../../hooks/useUserSession';

export const useEligibleCandidates = () => {
	const { srcmId } = useAuth();
	const { selectedRole } = useUserSession();
	const { setLoading } = useLoadingIndicator();
	const { showToastMessage } = useToastMessage();
	const { callApi } = useCallApi();
	const { batchId } = useBatchId();
	const { infoPopUpAction } = useInfoPopup();
	const { confirmPopUpAction } = useConfirmPopup();
	const navigate = useNavigate();
	const filterStateSnap = useSnapshot(EligibleCandidateListState);
	const [applicationSummaryData, setApplicationSummaryData] =
		useState<GetEligibleCandidatesForNextBatchResponse>({
			applicationSummary: [],
			pagination: { totalNoOfRecords: 0 },
		});
	const [batchData, setBatchData] = useState<GetBatchesResponse>({
		batches: [],
		pagination: { totalNoOfRecords: 0 },
	});
	const [eligibleCandidatesIds, setEligibleCandidatesIds] = useState<
		number[] | undefined
	>([]);
	const [isFilterTextApplied, setIsFilterTextApplied] =
		useState<boolean>(false);
	const [applicationPaging, setApplicationPaging] = useState<PagingConfig>(
		() => {
			return { ...filterStateSnap.paging } as PagingConfig;
		}
	);
	const [eligibleCandidatesFilter, setEligibleCandidatesFilter] =
		useState<ApplicationsFilter>(() => {
			if (filterStateSnap.filterBy) {
				return { ...filterStateSnap.filterBy } as ApplicationsFilter;
			} else {
				return {
					batchIds: [],
				};
			}
		});

	const previousBatchFilter: BatchFilter = useMemo(
		() => ({
			status: 'INACTIVE',
			sortingOrder: 'desc',
		}),
		[]
	);

	const onApplicationPageChange = useCallback(
		(pageNumber: number, pageSize: number) => {
			setApplicationPaging({ pageNo: pageNumber, pageSize });
		},
		[]
	);

	const onEligibleCandidatesChange = useCallback(
		(filterParam: ApplicationsFilter) => {
			const {
				searchByApplicantName,
				gender,
				searchByProposerSrcmId,
				status,
				regionIds,
				zoneNames,
			} = filterParam;

			if (
				searchByApplicantName ||
				gender ||
				searchByProposerSrcmId ||
				status ||
				regionIds ||
				zoneNames
			) {
				setIsFilterTextApplied(true);
			} else {
				setIsFilterTextApplied(false);
			}

			setEligibleCandidatesFilter(filterParam);
		},
		[]
	);

	const fetchPreviousBatchesForDropdown = useCallback(
		async (pageNumber: number, pageSize: number, filter: BatchFilter) => {
			if (srcmId) {
				setLoading(true);
				const pageIndex = pageNumber < 1 ? 0 : pageNumber - 1;
				try {
					const batchDropDownData = await getBatches({
						functionarySrcmId: srcmId,
						pageIndex,
						pageSize,
						batchFilter: filter,
					});
					setBatchData(batchDropDownData);
				} catch (e) {
					if (e instanceof Error) {
						showToastMessage('error', e.message);
					} else {
						showToastMessage('error', 'Unknown error occurred');
					}
				} finally {
					setLoading(false);
				}
			}
		},
		[setLoading, showToastMessage, srcmId]
	);

	const fetchEligibleCandidateFromPreviousBatchData = useCallback(
		async (
			pageNumber: number,
			pageSize: number,
			filter: ApplicationsFilter
		) => {
			if (srcmId && selectedRole) {
				setLoading(true);
				const pageIndex = pageNumber < 1 ? 0 : pageNumber - 1;
				try {
					const batchDataWithoutKey = await getEligibleCandidatesForNextBatch({
						functionarySrcmId: srcmId,
						pageIndex,
						pageSize,
						batchId,
						applicationsFilter: filter,
						selectedRoleCode: selectedRole,
					});
					const batchFetchedData = {
						...batchDataWithoutKey,
						...(batchDataWithoutKey.applicationSummary && {
							candidateBatches: batchDataWithoutKey.applicationSummary.map(
								(application) => ({
									...application,
									key: application.applicationId,
								})
							),
						}),
					};

					if (
						batchFetchedData.pagination.totalNoOfPages &&
						batchFetchedData.pagination.totalNoOfPages > 0 &&
						pageNumber > batchFetchedData.pagination.totalNoOfPages
					) {
						onApplicationPageChange(
							batchFetchedData.pagination.totalNoOfPages ?? 1,
							pageSize
						);
					} else {
						setApplicationSummaryData(batchFetchedData);
					}
				} catch (e) {
					if (e instanceof Error) {
						showToastMessage('error', e.message);
					} else {
						showToastMessage('error', 'Unknown error occurred');
					}
				} finally {
					setLoading(false);
				}
			}
		},
		[
			srcmId,
			selectedRole,
			setLoading,
			batchId,
			onApplicationPageChange,
			showToastMessage,
		]
	);

	const onConfirmSubmit = useCallback(async () => {
		if (srcmId && selectedRole && eligibleCandidatesIds) {
			setLoading(true);
			const request = {
				functionarySrcmId: srcmId,
				eligibleBatchId: batchId,
				requestedApplicationIds: eligibleCandidatesIds,
				selectedRoleCode: selectedRole,
			};

			try {
				await callApi(
					() => sendEmailToEligibleCandidatesForNextBatch(request),
					'errorOccurredWhileSubmittingApplication'
				);

				infoPopUpAction(
					'Mail Sent Successfully',
					'',
					() => {
						navigate(0);
					},
					ConfirmTick
				);
			} catch (e) {
				// handled in callApi
			} finally {
				setLoading(false);
			}
		}
	}, [
		srcmId,
		selectedRole,
		eligibleCandidatesIds,
		setLoading,
		batchId,
		callApi,
		infoPopUpAction,
		navigate,
	]);

	const onSubmitClick = useCallback(() => {
		confirmPopUpAction(
			'confirmSubmit',
			'areYouSureYouWantToSubmit',
			onConfirmSubmit
		);
	}, [onConfirmSubmit, confirmPopUpAction]);

	useEffect(() => {
		void fetchEligibleCandidateFromPreviousBatchData(
			applicationPaging.pageNo ?? 1,
			applicationPaging.pageSize ?? DEFAULT_PAGE_SIZE,
			eligibleCandidatesFilter
		);
	}, [
		fetchEligibleCandidateFromPreviousBatchData,
		applicationPaging,
		eligibleCandidatesFilter,
	]);

	useEffect(() => {
		void fetchPreviousBatchesForDropdown(0, 2000, previousBatchFilter);
	}, [fetchPreviousBatchesForDropdown, previousBatchFilter]);

	return {
		eligibleCandidatesFilter,
		applicationSummaryData,
		applicationPaging,
		onEligibleCandidatesChange,
		onApplicationPageChange,
		setEligibleCandidatesIds,
		onSubmitClick,
		batchData,
		isFilterTextApplied,
		setIsFilterTextApplied,
	};
};
